import { useState, useEffect } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "@/shared/components/ui/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { ArrowLeft, Save, User, Key, Shield, Copy, Eye, EyeOff, CheckCircle2, Zap, ChevronRight } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const PROVIDERS = [
  { code: "SCB", name: "Siam Commercial Bank" },
  { code: "KBANK", name: "Kasikorn Bank" },
  { code: "BBL", name: "Bangkok Bank" },
  { code: "KTB", name: "Krungthai Bank" },
  { code: "TRUEWALLET", name: "TrueMoney Wallet" },
  { code: "PROMPTPAY", name: "PromptPay QR" },
  { code: "LINEPAY", name: "LINE Pay" },
  { code: "SHOPEEPAY", name: "ShopeePay" },
];

const TABS = [
  { id: "general", label: "General", icon: User },
  { id: "fees", label: "Fees & Limits", icon: Zap },
  // { id: "permissions", label: "Permissions", icon: Lock },
  { id: "api", label: "API Access", icon: Key },
];

function genKey(pfx = "eng_pk_") {
  return pfx + Array.from({ length: 32 }, () =>
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]
  ).join("");
}

const FORM_CSS = `
  .f-wrap { --c: #22d3ee; --cb: rgba(34,211,238,0.18); --cs: rgba(34,211,238,0.08); --bg: #060f1e; --card: #0d1f35; --border: #1e3a5f; --t1: #eff6ff; --t2: #93b4d4; --t3: #4d6e90; }
  .f-card { background: linear-gradient(135deg,#0d1f35,#091525); border:1px solid #1e3a5f; border-radius:16px; overflow:hidden; transition:border-color .2s; }
  .f-card:hover { border-color: rgba(34,211,238,0.2); }
  .f-card-hd { padding:18px 22px; border-bottom:1px solid #1e3a5f; background:rgba(34,211,238,0.03); display:flex; align-items:center; gap:10px; }
  .f-card-title { font-size:14px; font-weight:700; color:#eff6ff; display:flex; align-items:center; gap:8px; }
  .f-card-title::before { content:''; width:3px; height:15px; background:linear-gradient(180deg,#22d3ee,#3b82f6); border-radius:2px; flex-shrink:0; }
  .f-card-desc { font-size:12px; color:#4d6e90; margin-top:3px; padding-left:11px; }
  .f-card-bd { padding:20px 22px; }
  .f-lbl { font-size:11px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.07em; display:block; margin-bottom:6px; }
  .f-inp { background:#060f1e; border:1px solid #1e3a5f; color:#eff6ff; border-radius:10px; padding:10px 14px; font-size:14px; width:100%; outline:none; transition:all .2s; font-family:inherit; }
  .f-inp:focus { border-color:#22d3ee; box-shadow:0 0 0 3px rgba(34,211,238,0.1); }
  .f-inp::placeholder { color:#2d4a6a; }
  .f-inp:disabled { opacity:.4; cursor:not-allowed; }
  .f-inp:-webkit-autofill, .f-inp:-webkit-autofill:focus { -webkit-box-shadow:0 0 0 1000px #060f1e inset!important; -webkit-text-fill-color:#eff6ff!important; }
  .f-textarea { background:#060f1e; border:1px solid #1e3a5f; color:#eff6ff; border-radius:10px; padding:10px 14px; font-size:14px; width:100%; outline:none; transition:all .2s; font-family:inherit; resize:vertical; }
  .f-textarea:focus { border-color:#22d3ee; box-shadow:0 0 0 3px rgba(34,211,238,0.1); }
  .f-textarea::placeholder { color:#2d4a6a; }
  .has-error { border-color: #ef4444 !important; }
  .f-eye-wrap { position:relative; }
  .f-eye-wrap .f-inp { padding-right:42px; }
  .f-eye { position:absolute; right:0; top:0; height:100%; width:40px; display:flex; align-items:center; justify-content:center; background:transparent; border:none; color:#4d6e90; cursor:pointer; transition:color .2s; }
  .f-eye:hover { color:#22d3ee; }
  .f-g2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
  .f-g3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:16px; }
  @media(max-width:640px){ .f-g2,.f-g3 { grid-template-columns:1fr; } }
  .f-help { font-size:12px; color:#2d4a6a; margin-top:4px; }
  .f-field { margin-bottom:16px; }
  .f-field:last-child { margin-bottom:0; }
  .f-prov-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(155px,1fr)); gap:8px; }
  .f-prov { display:flex; align-items:center; gap:10px; padding:11px 13px; border-radius:10px; border:1px solid #1e3a5f; cursor:pointer; transition:all .2s; background:#060f1e; user-select:none; }
  .f-prov:hover { border-color:rgba(34,211,238,0.35); background:rgba(34,211,238,0.06); }
  .f-prov.on { border-color:rgba(34,211,238,0.4); background:rgba(34,211,238,0.1); }
  .f-cb { width:16px; height:16px; border-radius:4px; border:2px solid #2d4a6a; flex-shrink:0; display:flex; align-items:center; justify-content:center; transition:all .15s; }
  .f-cb.on { background:#22d3ee; border-color:#22d3ee; }
  .f-prov-name { font-size:12px; font-weight:600; color:#64748b; }
  .f-prov-code { font-size:10px; color:#2d4a6a; margin-top:1px; }
  .f-prov.on .f-prov-name { color:#22d3ee; }
  .f-perm-row { display:flex; align-items:center; justify-content:space-between; padding:13px 15px; border:1px solid #1e3a5f; border-radius:12px; margin-bottom:8px; background:#060f1e; transition:all .2s; }
  .f-perm-row:last-child { margin-bottom:0; }
  .f-perm-row:hover { border-color:rgba(34,211,238,0.25); background:rgba(34,211,238,0.04); }
  .f-perm-label { font-size:13px; font-weight:600; color:#e2e8f0; }
  .f-perm-desc { font-size:11px; color:#4d6e90; margin-top:2px; }
  .f-warn { background:rgba(251,191,36,0.07); border:1px solid rgba(251,191,36,0.2); border-radius:12px; padding:13px 15px; display:flex; gap:10px; margin-bottom:18px; }
  .f-warn-txt { font-size:13px; color:#b8893d; }
  .f-code { flex:1; padding:10px 13px; background:#060f1e; border:1px solid #1e3a5f; border-radius:10px; font-family:'Fira Code','Courier New',monospace; font-size:12px; color:#22d3ee; overflow-x:auto; white-space:nowrap; }
  .f-icon-btn { width:36px; height:36px; border-radius:8px; background:#060f1e; border:1px solid #1e3a5f; color:#4d6e90; cursor:pointer; transition:all .2s; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .f-icon-btn:hover { border-color:rgba(34,211,238,0.4); color:#22d3ee; background:rgba(34,211,238,0.08); }
  .f-btn-primary { background:linear-gradient(135deg,#22d3ee,#0891b2); color:#04111f; border:none; border-radius:10px; padding:10px 22px; font-size:14px; font-weight:700; display:flex; align-items:center; gap:8px; cursor:pointer; transition:all .2s; box-shadow:0 4px 14px rgba(34,211,238,0.28); font-family:inherit; }
  .f-btn-primary:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 6px 20px rgba(34,211,238,0.4); }
  .f-btn-primary:disabled { opacity:.45; cursor:not-allowed; transform:none; }
  .f-btn-outline { background:transparent; color:#64748b; border:1px solid #1e3a5f; border-radius:10px; padding:10px 20px; font-size:14px; font-weight:500; cursor:pointer; transition:all .2s; text-decoration:none; display:inline-flex; align-items:center; gap:6px; font-family:inherit; }
  .f-btn-outline:hover { border-color:rgba(34,211,238,0.3); color:#93b4d4; background:rgba(34,211,238,0.05); }
  .f-btn-ghost { background:transparent; color:#4d6e90; border:1px solid #1e3a5f; border-radius:10px; padding:9px; cursor:pointer; transition:all .2s; display:flex; align-items:center; justify-content:center; }
  .f-btn-ghost:hover { border-color:rgba(34,211,238,0.35); color:#22d3ee; background:rgba(34,211,238,0.08); }
  .f-footer { display:flex; align-items:center; justify-content:flex-end; gap:12px; padding-top:20px; border-top:1px solid #1e3a5f; margin-top:4px; }
  .f-tab-strip { display:flex; gap:4px; background:#0d1f35; border:1px solid #1e3a5f; border-radius:14px; padding:5px; width:fit-content; flex-wrap:wrap; }
  .f-tab { display:inline-flex; align-items:center; gap:6px; padding:8px 16px; border-radius:10px; font-size:13px; font-weight:500; border:1px solid transparent; background:transparent; color:#4d6e90; cursor:pointer; transition:all .22s; white-space:nowrap; font-family:inherit; position:relative; overflow:hidden; }
  .f-tab:hover { color:#22d3ee; background:rgba(34,211,238,0.06); border-color:rgba(34,211,238,0.15); }
  .f-tab.on { color:#22d3ee; background:rgba(34,211,238,0.12); border-color:rgba(34,211,238,0.32); box-shadow:0 0 20px rgba(34,211,238,0.12); }
  .f-tab.on::after { content:''; position:absolute; bottom:0; left:8px; right:8px; height:2px; border-radius:99px; background:linear-gradient(90deg,transparent,rgba(34,211,238,0.7),transparent); }
  .f-tab-dot { width:5px; height:5px; border-radius:50%; background:#22d3ee; box-shadow:0 0 8px rgba(34,211,238,0.8); }
  .f-select-dark { background:#060f1e!important; border:1px solid #1e3a5f!important; color:#eff6ff!important; border-radius:10px!important; height:40px!important; }
  [data-radix-select-content] { background:#0d1f35!important; border:1px solid #1e3a5f!important; border-radius:12px!important; box-shadow:0 16px 48px rgba(0,0,0,0.6)!important; }
  [data-radix-select-item] { color:#94a3b8!important; font-size:13px!important; }
  [data-radix-select-item]:focus,[data-radix-select-item][data-highlighted] { background:rgba(34,211,238,0.08)!important; color:#22d3ee!important; }
`;

export default function AdminEngineForm() {
  const { t } = useLanguage();
  const urlParams = new URLSearchParams(window.location.search);
  const engineId = urlParams.get("id");
  const isEdit = !!engineId;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("general");

  const [form, setForm] = useState({
    name: "",
    code: "",
    email: "",
    password: "",
    status: "ACTIVE",
    contact_name: "",
    contact_phone: "",
    support_email: "",
    notes: "",
    deposit_fee: 0,
    withdrawal_fee: 0,
    max_resellers: 50,
    max_merchants: 100,
    allowed_providers: [],
    // permissions: {
    //   can_create_resellers: true, can_create_merchants: true,
    //   can_edit_fees: false, can_view_webhook_logs: true, can_regenerate_api_keys: true,
    // },
    api_key: genKey("eng_pk_"),
    secret_key: genKey("eng_sk_"),
  });

  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showApi, setShowApi] = useState(false);
  const [showSec, setShowSec] = useState(false);
  const [copied, setCopied] = useState(null);
  const [errors, setErrors] = useState({});

  const { data: existing } = useQuery({
    queryKey: ["engine", engineId],
    queryFn: () => adminService.getEngineById(engineId),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!existing) return;
    setForm(p => ({
      ...p,
      name: existing.name,
      code: existing.code || "",
      email: existing.email,
      password: "",
      status: existing.status,
      contact_name: existing.contactName ?? "",
      contact_phone: existing.contactPhone ?? "",
      support_email: existing.supportEmail ?? "",
      notes: existing.notes ?? "",

      deposit_fee: existing.depositFee ?? 0,
      withdrawal_fee: existing.withdrawalFee ?? 0,
      percentage_fee: existing.percentageFee ?? 1.0,
      allowed_providers: existing.allowedProviders ?? [],
      max_resellers: existing.maxResellers ?? 50,
      max_merchants: existing.maxMerchants ?? 100,

      api_key: existing.apiKey ?? p.api_key,
      secret_key: existing.secretKey ?? p.secret_key,
    }));
  }, [existing]);

  const createMut = useMutation({
    mutationFn: d => adminService.createEngine(d),
    onSuccess: () => { queryClient.invalidateQueries(["admin-engines"]); navigate(createPageUrl("AdminEngines")); },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || "Something went wrong";
      toast({ title: "Error", description: message, variant: "destructive" });
    }
  });

  const updateMut = useMutation({
    mutationFn: d => adminService.updateEngine(engineId, d),
    onSuccess: () => { queryClient.invalidateQueries(["admin-engines"]); queryClient.invalidateQueries(["engine", engineId]); navigate(createPageUrl("AdminEngines")); },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || "Something went wrong";
      toast({ title: "Error", description: message, variant: "destructive" });
    }
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleProv = c => set("allowed_providers", form.allowed_providers.includes(c) ? form.allowed_providers.filter(x => x !== c) : [...form.allowed_providers, c]);
  const copy = (text, type) => { navigator.clipboard.writeText(text); setCopied(type); setTimeout(() => setCopied(null), 2000); };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!form.name) newErrors.name = true;
    if (!form.code) newErrors.code = true;
    if (!form.email) newErrors.email = true;
    if (!isEdit && !form.password) newErrors.password = true;
    if (!form.api_key) newErrors.api_key = true;
    if (!form.secret_key) newErrors.secret_key = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast({ title: t("pleaseFillAllRequiredFields") || "Please fill all required fields", variant: "destructive" });
      return;
    }
    setErrors({});

    setSaving(true);
    try {
      if (isEdit) {
        const updateData = {
          name: form.name,
          status: form.status,
          code: form.code,
          contactName: form.contact_name || undefined,
          contactPhone: form.contact_phone || undefined,
          supportEmail: form.support_email || undefined,
          notes: form.notes || undefined,
          depositFee: form.deposit_fee,
          withdrawalFee: form.withdrawal_fee,
          maxResellers: form.max_resellers,
          maxMerchants: form.max_merchants,
          allowedProviders: form.allowed_providers,
          apiKey: form.api_key || undefined,
          secretKey: form.secret_key || undefined,
        };
        if (form.password) updateData.password = form.password;
        await updateMut.mutateAsync(updateData);
      } else {
        const createData = {
          email: form.email,
          password: form.password,
          name: form.name,
          code: form.code,
          contactName: form.contact_name || undefined,
          contactPhone: form.contact_phone || undefined,
          supportEmail: form.support_email || undefined,
          notes: form.notes || undefined,
          depositFee: form.deposit_fee,
          withdrawalFee: form.withdrawal_fee,
          maxResellers: form.max_resellers,
          maxMerchants: form.max_merchants,
          allowedProviders: form.allowed_providers,
          apiKey: form.api_key,
          secretKey: form.secret_key,
        };
        await createMut.mutateAsync(createData);
      }
    } catch { setSaving(false); }
  };

  // const PERMS = [
  //   { k: "can_create_resellers", label: t("canCreateResellers"), desc: t("allowCreatingNewResellerAccounts") },
  //   { k: "can_create_merchants", label: t("canCreateMerchantsDirect"), desc: t("allowCreatingMerchantAccountsDirectly") },
  //   { k: "can_edit_fees", label: t("canEditFeeStructure"), desc: t("allowModifyingFeeSettings") },
  //   { k: "can_view_webhook_logs", label: t("canViewWebhookLogs"), desc: t("accessToRawWebhookData") },
  //   { k: "can_regenerate_api_keys", label: t("canRegenerateApiKeys"), desc: t("allowRegeneratingApiCredentials") },
  // ];

  return (
    <div className="f-wrap" style={{ maxWidth: 900, paddingBottom: 32 }}>
      <style>{FORM_CSS}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 26 }}>
        <Link to={createPageUrl("AdminEngines")}>
          <button className="f-btn-ghost"><ArrowLeft size={18} /></button>
        </Link>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: "linear-gradient(135deg,rgba(34,211,238,0.18),rgba(59,130,246,0.18))", border: "1px solid rgba(34,211,238,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Zap size={20} style={{ color: "#22d3ee" }} />
            </div>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: "#eff6ff", margin: 0, letterSpacing: "-0.02em" }}>{isEdit ? t("editEngine") : t("createNewEngine")}</h1>
              <p style={{ fontSize: 13, color: "#4d6e90", margin: 0 }}>{isEdit ? t("updateEngineDetails") : t("setUpNewEnginePartner")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Strip */}
      <div className="f-tab-strip" style={{ marginBottom: 22 }}>
        {TABS.map(tab => (
          <button key={tab.id} className={`f-tab${activeTab === tab.id ? " on" : ""}`} onClick={() => setActiveTab(tab.id)}>
            <tab.icon size={14} />
            {tab.label}
            {activeTab === tab.id && <span className="f-tab-dot" />}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.22 }}>

          {/* GENERAL */}
          {activeTab === "general" && (
            <div className="f-card">
              <div className="f-card-hd"><div className="f-card-title"><User size={15} />{t("generalInformation")}</div></div>
              <div className="f-card-bd">
                <div className="f-g2 f-field">
                  <div><label className="f-lbl">{t("businessNameRequired")}</label><input className={`f-inp ${errors.name ? 'has-error' : ''}`} value={form.name} onChange={e => { set("name", e.target.value); setErrors(prev => ({ ...prev, name: false })); }} placeholder={t("businessNamePlaceholder")} /></div>
                  <div><label className="f-lbl">{t("engineCode")}</label><input className={`f-inp ${errors.code ? 'has-error' : ''}`} value={form.code} onChange={e => { set("code", e.target.value.toUpperCase()); setErrors(prev => ({ ...prev, code: false })); }} placeholder="ENG001" /></div>
                </div>
                <div className="f-g2 f-field">
                  <div>
                    <label className="f-lbl">{t("loginEmailRequired")}</label>
                    <input autoComplete="new-password" className={`f-inp ${errors.email ? 'has-error' : ''}`} type="email" value={form.email} onChange={e => { set("email", e.target.value); setErrors(prev => ({ ...prev, email: false })); }} placeholder={t("emailPlaceholder") || "Email address"} disabled={isEdit} />
                    {isEdit && <p className="f-help">{t("emailCannotBeChanged")}</p>}
                  </div>
                  <div>
                    <label className="f-lbl">{isEdit ? t("newPasswordOptional") : t("passwordRequired")}</label>
                    <div className="f-eye-wrap">
                      <input autoComplete="new-password" className={`f-inp ${errors.password ? 'has-error' : ''}`} type={showPwd ? "text" : "password"} value={form.password} onChange={e => { set("password", e.target.value); setErrors(prev => ({ ...prev, password: false })); }} placeholder={isEdit ? t("leaveBlankToKeepCurrent") : t("enterPassword")} />
                      <button type="button" className="f-eye" onClick={() => setShowPwd(!showPwd)}>{showPwd ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                    </div>
                  </div>
                </div>
                {isEdit && (
                  <div className="f-field">
                    <label className="f-lbl">{t("status")}</label>
                    <Select value={form.status} onValueChange={v => set("status", v)}>
                      <SelectTrigger className="f-select-dark"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE">{t("active")}</SelectItem>
                        <SelectItem value="INACTIVE">{t("inactive")}</SelectItem>
                        <SelectItem value="SUSPENDED">{t("suspended")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="f-g2 f-field">
                  <div><label className="f-lbl">{t("contactName")}</label><input className="f-inp" value={form.contact_name} onChange={e => set("contact_name", e.target.value)} /></div>
                  <div><label className="f-lbl">{t("contactPhone")}</label><input className="f-inp" value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} /></div>
                </div>
                <div className="f-field"><label className="f-lbl">{t("supportEmail")}</label><input className="f-inp" type="email" value={form.support_email} onChange={e => set("support_email", e.target.value)} /></div>
                <div><label className="f-lbl">{t("notes")}</label><textarea className="f-textarea" value={form.notes} onChange={e => set("notes", e.target.value)} rows={3} /></div>
              </div>
            </div>
          )}

          {/* FEES */}
          {activeTab === "fees" && (
            <div className="f-card">
              <div className="f-card-hd">
                <div>
                  <div className="f-card-title"><Zap size={15} />{t("feesLimitsTab")}</div>
                  <div className="f-card-desc">{t("configureFeeStructureLimits")}</div>
                </div>
              </div>
              <div className="f-card-bd">
                <div className="f-g3 f-field">
                  <div><label className="f-lbl">{t("depositFeeThb")}</label><input className="f-inp" type="number" step="0.01" value={form.deposit_fee} onChange={e => set("deposit_fee", parseFloat(e.target.value))} /></div>
                  <div><label className="f-lbl">{t("withdrawalFeeThb")}</label><input className="f-inp" type="number" step="0.01" value={form.withdrawal_fee} onChange={e => set("withdrawal_fee", parseFloat(e.target.value))} /></div>
                  <div><label className="f-lbl">{t("maxResellers")}</label><input className="f-inp" type="number" value={form.max_resellers} onChange={e => set("max_resellers", parseInt(e.target.value))} /></div>
                  <div><label className="f-lbl">{t("maxMerchants")}</label><input className="f-inp" type="number" value={form.max_merchants} onChange={e => set("max_merchants", parseInt(e.target.value))} /></div>
                </div>
                <div>
                  <label className="f-lbl" style={{ marginBottom: 10 }}>{t("allowedPaymentProviders")}</label>
                  <div className="f-prov-grid">
                    {PROVIDERS.map(p => {
                      const on = form.allowed_providers?.includes(p.code);
                      return (
                        <div key={p.code} className={`f-prov${on ? " on" : ""}`} onClick={() => toggleProv(p.code)}>
                          <div className={`f-cb${on ? " on" : ""}`}>{on && <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3" stroke="#04111f" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}</div>
                          <div><div className="f-prov-name">{p.name}</div><div className="f-prov-code">{p.code}</div></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PERMISSIONS */}
          {/* {activeTab === "permissions" && (
            <div className="f-card">
              <div className="f-card-hd">
                <div>
                  <div className="f-card-title"><Lock size={15} />{t("permissionsTab")}</div>
                  <div className="f-card-desc">{t("controlEngineActions")}</div>
                </div>
              </div>
              <div className="f-card-bd">
                {PERMS.map(p => (
                  <div key={p.k} className="f-perm-row">
                    <div><div className="f-perm-label">{p.label}</div><div className="f-perm-desc">{p.desc}</div></div>
                    <Switch checked={form.permissions?.[p.k] || false} onCheckedChange={v => setPerm(p.k, v)} />
                  </div>
                ))}
              </div>
            </div>
          )} */}

          {/* API */}
          {activeTab === "api" && (
            <div className="f-card">
              <div className="f-card-hd">
                <div>
                  <div className="f-card-title"><Key size={15} />{t("apiCredentials")}</div>
                  <div className="f-card-desc">{t("shareCredentialsSecurely")}</div>
                </div>
              </div>
              <div className="f-card-bd">
                <div className="f-warn">
                  <Shield size={16} style={{ color: "#f5a623", flexShrink: 0, marginTop: 2 }} />
                  <span className="f-warn-txt">{t("keepCredentialsSecure")}</span>
                </div>
                {[
                  { label: t("apiKey"), show: showApi, toggle: () => setShowApi(!showApi), val: form.api_key, type: "api_key", copyType: "api" },
                  { label: t("secretKey"), show: showSec, toggle: () => setShowSec(!showSec), val: form.secret_key, type: "secret_key", copyType: "secret" },
                ].map(item => (
                  <div key={item.type} style={{ marginBottom: 18 }}>
                    <label className="f-lbl" style={{ marginBottom: 8 }}>{item.label}</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div className={`f-code ${errors[item.type] ? 'has-error' : ''}`}>{item.show ? item.val : "••••••••••••••••••••••••••••••••"}</div>
                      <button type="button" className="f-icon-btn" onClick={item.toggle}>{item.show ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                      <button type="button" className="f-icon-btn" onClick={() => copy(item.val, item.copyType)}>
                        {copied === item.copyType ? <CheckCircle2 size={14} style={{ color: "#10b981" }} /> : <Copy size={14} />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </motion.div>
      </AnimatePresence>

      {/* Footer */}
      <div className="f-footer">
        <Link to={createPageUrl("AdminEngines")}><button type="button" className="f-btn-outline">{t("cancel")}</button></Link>
        <button className="f-btn-primary" onClick={handleSubmit} disabled={saving}>
          <Save size={15} />
          {saving ? t("saving") : isEdit ? t("updateEngine") : t("createEngine")}
        </button>
      </div>
    </div>
  );
}